import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import { MeetingReport } from "@app/common/types";
import { omit } from "@app/common/utils";
import { parseUTCDateTime } from "@app/utils/utils";

import {
  ApiPostCallReport,
  ApiPostCallReportTask,
  TimeSeriesRecordType,
} from "../types";

const roundMinutes = (date: DateTime): DateTime =>
  date.plus({ seconds: 30 }).startOf("minute");

const findTimeSeries =
  (data: ApiPostCallReport["timeSeriesMetrics"]) =>
  <T extends ApiPostCallReport["timeSeriesMetrics"][number]["label"]>(
    label: T
  ): TimeSeriesRecordType[T]["data"] | undefined => {
    const seriesData = data.find(
      (series) => series.label === label
    ) as TimeSeriesRecordType[T];

    return seriesData?.data;
  };

const DEFAULT_OVERALL = {
  sentiment: undefined,
  engagement: undefined,
  callScore: undefined,
};

export const getMeetingReportFromResponse = (
  data: ApiPostCallReport | ApiPostCallReportTask,
  sessionId: string
): MeetingReport => {
  // a JSON response with a single "task" field will be returned from the metrics API
  // when there are no metrics for a meeting
  if (isEmpty(data) || "task" in data) {
    return { meetingId: sessionId, insufficientData: true };
  }

  const processing = isEmpty(omit("sessionId", data));
  if (processing) {
    return { meetingId: sessionId, insufficientData: true };
  }

  const callDate = parseUTCDateTime(data.callDate);
  const rawStartTime = data.callDate;
  const rawEndTime = data.endTime;
  const startTime = roundMinutes(parseUTCDateTime(data.startTime));
  const endTime = roundMinutes(parseUTCDateTime(data.endTime));
  const timeSeriesMetrics = data.timeSeriesMetrics ?? [];
  const overallData = data.overall ?? DEFAULT_OVERALL;

  const getSeriesData = findTimeSeries(timeSeriesMetrics);

  const talkTimeData = getSeriesData("speakers");
  const callScoreTrend = getSeriesData("call_score");
  const sentimentTrend = getSeriesData("sentiment_score");
  const engagementTrend = getSeriesData("engagement_score");

  return {
    meetingId: data.sessionId,
    callDate,
    rawStartTime,
    rawEndTime,
    startTime,
    endTime,
    talkTimeData,
    callScoreTrend,
    sentimentTrend,
    engagementTrend,
    participants: data.participants,
    callScore: overallData.callScore,
    sentiment: overallData.sentiment,
    engagement: overallData.engagement,
    insufficientData: false,
  };
};
